/**
 * rowGrouping.ts
 *
 * Pure row projection helpers for DataTable consumers that need canvas-rendered
 * group/tree rows without replacing the table engine. The projected rows carry
 * metadata that custom DataTable columns can render as disclosure controls,
 * depth indentation, and aggregate labels.
 */

import type { MinimalModel } from "@/endlaFunctions/customTypings/models";

export enum DataTableProjectedRowKind {
  Data = "data",
  Group = "group",
}

export enum DataTableAggregateFunction {
  Count = "count",
  Sum = "sum",
  Min = "min",
  Max = "max",
  Latest = "latest",
  Earliest = "earliest",
}

export const DATA_TABLE_PROJECTED_ROW_KIND_FIELD = "__dataTableProjectedRowKind";
export const DATA_TABLE_GROUP_KEY_FIELD = "__dataTableGroupKey";
export const DATA_TABLE_GROUP_DEPTH_FIELD = "__dataTableDepth";
export const DATA_TABLE_GROUP_CHILD_COUNT_FIELD = "__dataTableChildCount";
export const DATA_TABLE_GROUP_AGGREGATES_FIELD = "__dataTableAggregateValues";
export const DATA_TABLE_GROUP_EXPANDED_FIELD = "__dataTableIsExpanded";
export const DATA_TABLE_GROUP_SORT_FIELD = "data_table_group_sort";
export const DATA_TABLE_GROUP_ROW_SORT_FIELD = "data_table_group_row_sort";

export const DATA_TABLE_GROUP_SORT_PREFIX = [
  { id: DATA_TABLE_GROUP_SORT_FIELD, desc: false },
  { id: DATA_TABLE_GROUP_ROW_SORT_FIELD, desc: false },
] as const;

type DataTableSortEntry = {
  id: string;
  desc: boolean;
};

export type DataTableAggregateSpec<TRow extends MinimalModel> = {
  id: string;
  function: DataTableAggregateFunction;
  columnId?: Extract<keyof TRow, string>;
  getValue?: (row: TRow) => unknown;
};

export type DataTableProjectedDataRow<TRow extends MinimalModel> = TRow & {
  [DATA_TABLE_PROJECTED_ROW_KIND_FIELD]?: DataTableProjectedRowKind.Data;
  [DATA_TABLE_GROUP_DEPTH_FIELD]?: number;
  [DATA_TABLE_GROUP_SORT_FIELD]?: number;
  [DATA_TABLE_GROUP_ROW_SORT_FIELD]?: number;
  __dataTableParentGroupKey?: string;
};

export type DataTableProjectedGroupRow = MinimalModel & {
  [DATA_TABLE_PROJECTED_ROW_KIND_FIELD]: DataTableProjectedRowKind.Group;
  [DATA_TABLE_GROUP_KEY_FIELD]: string;
  [DATA_TABLE_GROUP_DEPTH_FIELD]: number;
  [DATA_TABLE_GROUP_CHILD_COUNT_FIELD]: number;
  [DATA_TABLE_GROUP_AGGREGATES_FIELD]: Record<string, unknown>;
  [DATA_TABLE_GROUP_EXPANDED_FIELD]: boolean;
  [DATA_TABLE_GROUP_SORT_FIELD]: number;
  [DATA_TABLE_GROUP_ROW_SORT_FIELD]: number;
  __dataTableGroupLabel: string;
  __dataTableGroupSourceRow?: MinimalModel;
  __dataTableGroupChildren: MinimalModel[];
};

export type DataTableStaticGroup<
  TRow extends MinimalModel,
  TGroupRow extends MinimalModel = MinimalModel,
> = {
  key: string;
  label: string;
  row: TGroupRow;
  childGroups?: readonly DataTableStaticGroup<TRow, TGroupRow>[];
  children: readonly TRow[];
  aggregateRows?: readonly TRow[];
  depth?: number;
};

export type ProjectDataTableStaticGroupsOptions<
  TRow extends MinimalModel,
  TGroupRow extends MinimalModel = MinimalModel,
> = {
  groups: readonly DataTableStaticGroup<TRow, TGroupRow>[];
  ungroupedRows?: readonly TRow[];
  expandedGroupKeys?: ReadonlySet<string>;
  defaultExpanded?: boolean;
  aggregates?: readonly DataTableAggregateSpec<TRow>[];
  preserveProjectedOrder?: boolean;
};

export type DataTableProjectedRow<TRow extends MinimalModel> =
  | DataTableProjectedDataRow<TRow>
  | DataTableProjectedGroupRow;

export function createDataTableGroupKey(namespace: string, key: PrimaryKey | string): string {
  return `${namespace}:${key}`;
}

export function withDataTableGroupSortPrefix(
  sorting: readonly DataTableSortEntry[]
): DataTableSortEntry[] {
  const groupSortColumnIds = new Set<string>(DATA_TABLE_GROUP_SORT_PREFIX.map(({ id }) => id));
  return [
    ...DATA_TABLE_GROUP_SORT_PREFIX,
    ...sorting.filter(({ id }) => !groupSortColumnIds.has(id)),
  ];
}

export function isDataTableGroupSortPrefixed(sorting: readonly DataTableSortEntry[]) {
  return DATA_TABLE_GROUP_SORT_PREFIX.every(
    (sort, index) => sorting[index]?.id === sort.id && sorting[index]?.desc === sort.desc
  );
}

export function areDataTableSortStatesEqual(
  left: readonly DataTableSortEntry[],
  right: readonly DataTableSortEntry[]
) {
  return (
    left.length === right.length &&
    left.every((sort, index) => sort.id === right[index]?.id && sort.desc === right[index]?.desc)
  );
}

export function isDataTableGroupRow(
  row: MinimalModel | null | undefined
): row is DataTableProjectedGroupRow {
  return (
    (row as Record<string, unknown> | null | undefined)?.[DATA_TABLE_PROJECTED_ROW_KIND_FIELD] ===
    DataTableProjectedRowKind.Group
  );
}

/**
 * All leaf rows in a group's subtree, for aggregation. A precomputed
 * `aggregateRows` overrides the walk (callers may aggregate over a wider/derived
 * set); otherwise recurse into every descendant group — not just direct children
 * and immediate child-groups — so 3+ level group totals are correct. (BUG-122)
 */
function collectAggregateRows<TRow extends MinimalModel, TGroupRow extends MinimalModel>(
  group: DataTableStaticGroup<TRow, TGroupRow>
): TRow[] {
  if (group.aggregateRows) {
    return [...group.aggregateRows];
  }
  return [
    ...group.children,
    ...(group.childGroups ?? []).flatMap((childGroup) => collectAggregateRows(childGroup)),
  ];
}

export function projectDataTableStaticGroups<
  TRow extends MinimalModel,
  TGroupRow extends MinimalModel = MinimalModel,
>({
  groups,
  ungroupedRows = [],
  expandedGroupKeys,
  defaultExpanded = false,
  aggregates = [],
  preserveProjectedOrder = false,
}: ProjectDataTableStaticGroupsOptions<TRow, TGroupRow>): DataTableProjectedRow<TRow>[] {
  const projectedRows: DataTableProjectedRow<TRow>[] = [];
  let groupSortIndex = 0;

  function appendGroup(group: DataTableStaticGroup<TRow, TGroupRow>) {
    const groupIndex = groupSortIndex;
    groupSortIndex += 1;
    const depth = group.depth ?? 0;
    const childGroups = [...(group.childGroups ?? [])];
    const directChildCount = childGroups.length + group.children.length;
    const aggregateRows = collectAggregateRows(group);
    const isExpanded = expandedGroupKeys?.has(group.key) ?? defaultExpanded;
    const groupRow = {
      ...group.row,
      [DATA_TABLE_PROJECTED_ROW_KIND_FIELD]: DataTableProjectedRowKind.Group,
      [DATA_TABLE_GROUP_KEY_FIELD]: group.key,
      [DATA_TABLE_GROUP_DEPTH_FIELD]: depth,
      [DATA_TABLE_GROUP_CHILD_COUNT_FIELD]: directChildCount,
      [DATA_TABLE_GROUP_AGGREGATES_FIELD]: computeAggregateValues(aggregateRows, aggregates),
      [DATA_TABLE_GROUP_EXPANDED_FIELD]: isExpanded,
      [DATA_TABLE_GROUP_SORT_FIELD]: groupIndex,
      [DATA_TABLE_GROUP_ROW_SORT_FIELD]: 0,
      __dataTableGroupLabel: group.label,
      __dataTableGroupSourceRow: group.row,
      __dataTableGroupChildren: [
        ...childGroups.map((childGroup) => childGroup.row),
        ...group.children,
      ],
    } satisfies DataTableProjectedGroupRow;

    projectedRows.push(groupRow);
    if (!isExpanded) {
      return;
    }

    for (const childGroup of childGroups) {
      appendGroup({ ...childGroup, depth: childGroup.depth ?? depth + 1 });
    }

    for (const child of group.children) {
      projectedRows.push({
        ...child,
        [DATA_TABLE_PROJECTED_ROW_KIND_FIELD]: DataTableProjectedRowKind.Data,
        [DATA_TABLE_GROUP_DEPTH_FIELD]: depth + 1,
        [DATA_TABLE_GROUP_SORT_FIELD]: groupIndex,
        [DATA_TABLE_GROUP_ROW_SORT_FIELD]: 1,
        __dataTableParentGroupKey: group.key,
      } satisfies DataTableProjectedDataRow<TRow>);
    }
  }

  for (const group of groups) {
    appendGroup(group);
  }

  for (const [ungroupedIndex, row] of ungroupedRows.entries()) {
    projectedRows.push({
      ...row,
      [DATA_TABLE_PROJECTED_ROW_KIND_FIELD]: DataTableProjectedRowKind.Data,
      [DATA_TABLE_GROUP_DEPTH_FIELD]: 0,
      [DATA_TABLE_GROUP_SORT_FIELD]: groupSortIndex + ungroupedIndex,
      [DATA_TABLE_GROUP_ROW_SORT_FIELD]: 0,
    } satisfies DataTableProjectedDataRow<TRow>);
  }

  if (preserveProjectedOrder) {
    let blockSortIndex = -1;
    for (const [index, row] of projectedRows.entries()) {
      const depth = Number(row[DATA_TABLE_GROUP_DEPTH_FIELD] ?? 0);
      if (depth === 0) {
        blockSortIndex += 1;
      }
      row[DATA_TABLE_GROUP_SORT_FIELD] = Math.max(blockSortIndex, 0);
      row[DATA_TABLE_GROUP_ROW_SORT_FIELD] = index;
    }
  }

  return projectedRows;
}

function computeAggregateValues<TRow extends MinimalModel>(
  rows: TRow[],
  aggregates: readonly DataTableAggregateSpec<TRow>[]
) {
  const values: Record<string, unknown> = {};
  for (const aggregate of aggregates) {
    values[aggregate.id] = computeAggregateValue(rows, aggregate);
  }
  return values;
}

function computeAggregateValue<TRow extends MinimalModel>(
  rows: TRow[],
  aggregate: DataTableAggregateSpec<TRow>
) {
  if (aggregate.function === DataTableAggregateFunction.Count) {
    return rows.length;
  }

  const values = rows
    .map((row) => resolveAggregateValue(row, aggregate))
    .filter((value) => value !== null && value !== undefined);

  switch (aggregate.function) {
    case DataTableAggregateFunction.Sum:
      return values.reduce<number>((total, value) => total + toNumber(value), 0);
    case DataTableAggregateFunction.Min:
      return values.reduce<unknown>(
        (current, value) =>
          current === undefined || compareAggregateValues(value, current) < 0 ? value : current,
        undefined
      );
    case DataTableAggregateFunction.Max:
      return values.reduce<unknown>(
        (current, value) =>
          current === undefined || compareAggregateValues(value, current) > 0 ? value : current,
        undefined
      );
    case DataTableAggregateFunction.Latest:
      return values.reduce<unknown>(
        (current, value) =>
          current === undefined || compareAggregateValues(value, current) > 0 ? value : current,
        undefined
      );
    case DataTableAggregateFunction.Earliest:
      return values.reduce<unknown>(
        (current, value) =>
          current === undefined || compareAggregateValues(value, current) < 0 ? value : current,
        undefined
      );
    default:
      return null;
  }
}

function resolveAggregateValue<TRow extends MinimalModel>(
  row: TRow,
  aggregate: DataTableAggregateSpec<TRow>
) {
  if (aggregate.getValue) {
    return aggregate.getValue(row);
  }
  return aggregate.columnId ? row[aggregate.columnId] : undefined;
}

function toNumber(value: unknown) {
  return typeof value === "number" ? value : Number(value) || 0;
}

function compareAggregateValues(left: unknown, right: unknown) {
  return toComparableValue(left) - toComparableValue(right);
}

function toComparableValue(value: unknown) {
  if (value instanceof Date) {
    return value.getTime();
  }
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string") {
    const timestamp = Date.parse(value);
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }
  return Number(value) || 0;
}
